import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import staffExtendedService, { TimeEntry } from '../../services/staffExtendedService';

interface TimeClockProps {
  staffMembers: any[];
}

const TimeClock: React.FC<TimeClockProps> = ({ staffMembers }) => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState('');
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);

  useEffect(() => {
    loadTimeEntries();
  }, []);

  const loadTimeEntries = async () => {
    try {
      setLoading(true);
      const { data } = await staffExtendedService.getTimeEntries();
      setTimeEntries(data);
      // Find any active entry (clocked in but not out)
      const active = data.find((e: TimeEntry) => e.clock_out === null);
      setActiveEntry(active || null);
    } catch (err: any) {
      toast.error('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    if (!selectedStaff) {
      toast.error('Please select a staff member');
      return;
    }
    try {
      await staffExtendedService.clockIn(selectedStaff);
      toast.success('Clocked in successfully');
      loadTimeEntries();
    } catch (err) {
      toast.error('Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    if (!activeEntry?.id) return;
    try {
      await staffExtendedService.clockOut(activeEntry.id);
      toast.success('Clocked out successfully');
      loadTimeEntries();
    } catch (err) {
      toast.error('Failed to clock out');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-800">Time Clock</h3>
      </div>

      {/* Clock In/Out Widget */}
      <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {activeEntry ? (
              <div>
                <p className="text-sm opacity-90">Currently Clocked In</p>
                <p className="text-3xl font-bold mt-2">
                  {staffMembers.find(s => s.id === activeEntry.staff_id)?.first_name} {staffMembers.find(s => s.id === activeEntry.staff_id)?.last_name}
                </p>
                <p className="text-sm mt-1">Since {new Date(activeEntry.clock_in).toLocaleTimeString()}</p>
              </div>
            ) : (
              <div>
                <p className="text-sm opacity-90">Clock In</p>
                <select
                  value={selectedStaff}
                  onChange={(e) => setSelectedStaff(e.target.value)}
                  className="mt-2 bg-white/20 backdrop-blur text-white border border-white/30 rounded-lg px-4 py-2 w-full max-w-xs"
                >
                  <option value="">Select Staff Member</option>
                  {staffMembers.map(staff => (
                    <option key={staff.id} value={staff.id} className="text-gray-900">
                      {staff.first_name} {staff.last_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <button
            onClick={activeEntry ? handleClockOut : handleClockIn}
            className="bg-white text-blue-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
          >
            {activeEntry ? 'Clock Out' : 'Clock In'}
          </button>
        </div>
      </div>

      {/* Recent Entries */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h4 className="font-semibold mb-4">Recent Time Entries</h4>
        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Staff</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Clock In</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Clock Out</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Hours</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {timeEntries.slice(0, 10).map(entry => (
                <tr key={entry.id}>
                  <td className="px-4 py-3 text-sm">
                    {staffMembers.find(s => s.id === entry.staff_id)?.first_name} {staffMembers.find(s => s.id === entry.staff_id)?.last_name}
                  </td>
                  <td className="px-4 py-3 text-sm">{new Date(entry.clock_in).toLocaleString()}</td>
                  <td className="px-4 py-3 text-sm">{entry.clock_out ? new Date(entry.clock_out).toLocaleString() : '-'}</td>
                  <td className="px-4 py-3 text-sm">{entry.total_hours ? `${entry.total_hours.toFixed(2)}h` : '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      entry.status === 'approved' ? 'bg-green-100 text-green-800' :
                      entry.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {entry.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TimeClock;
